
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { loaderFacts } from "@/data/loaderFacts";

interface HeavenlyLoaderProps {
  message?: string;
  onClose?: () => void;
}

const HeavenlyLoader: React.FC<HeavenlyLoaderProps> = ({ 
  message = "Loading Bible text...",
  onClose 
}) => {
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [isFactVisible, setIsFactVisible] = useState(true);
  
  useEffect(() => {
    const factInterval = setInterval(() => {
      setIsFactVisible(false);
      
      setTimeout(() => {
        setCurrentFactIndex(prev => {
          const nextIndex = Math.floor(Math.random() * loaderFacts.length);
          return nextIndex === prev ? (nextIndex + 1) % loaderFacts.length : nextIndex;
        });
        setIsFactVisible(true);
      }, 500);
      
    }, 5000);
    
    return () => clearInterval(factInterval);
  }, []);

  return (
    <Dialog open={true}>
      <DialogContent className="sm:max-w-md md:max-w-lg bg-gradient-to-br from-[#1E40AF] to-[#152C75] border-[#D4AF37]/30 p-0 overflow-hidden">
        {onClose && (
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 z-50 text-[#D4AF37]/80 hover:text-[#D4AF37] transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        )}
        
        <div className="relative flex flex-col items-center justify-center p-6 bg-gradient-to-t from-[#1E40AF]/50 to-transparent">          
          {/* Logo */}
          <div className="relative z-10 mb-6">
            <motion.img 
              src="https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//Corner%20logo%20(8).png"
              alt="Prayer Logo"
              className="relative w-24 h-24 object-contain"
              animate={{ 
                scale: [1, 1.05, 1],
                rotate: [0, 2, 0, -2, 0]
              }}
              transition={{ 
                repeat: Infinity, 
                duration: 3, 
                ease: "easeInOut" 
              }}
            />
          </div>

          {/* Loading message with gradient text */}
          <div className="mb-4 text-center relative z-10">
            <motion.p 
              className="bg-gradient-to-r from-[#D4AF37] to-[#BF9B30] bg-clip-text text-transparent font-poppins text-lg"
              animate={{ opacity: [0.7, 1, 0.7] }}
              transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
            >
              {message}
            </motion.p>
          </div>

          {/* Loading spinner with gold color */}
          <div className="mb-6 relative z-10">
            <Loader2 className="h-6 w-6 animate-spin text-[#D4AF37]" />
          </div>

          {/* Facts display with gradient text */}
          <div className="max-w-md px-4 text-center relative z-10">
            <AnimatePresence mode="wait">
              {isFactVisible && (
                <motion.div
                  key={currentFactIndex}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.5 }}
                  className="space-y-3"
                >
                  <h2 className="text-xl md:text-2xl font-playfair bg-gradient-to-r from-[#D4AF37] to-[#BF9B30] bg-clip-text text-transparent">{loaderFacts[currentFactIndex].text}</h2>
                  <p className="text-base md:text-lg text-white/90 font-poppins italic">
                    {loaderFacts[currentFactIndex].cta}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HeavenlyLoader;
